import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ChangeDetectionStrategy, Component, ComponentRef, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { ReplaySubject, takeUntil } from 'rxjs';

import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import {
  IncomingMessageType,
  OutgoingMessageType,
  ResizeMessage,
  SendTestUserCommandMessage,
  SetTestUserAdioMessage,
} from 'messages/websocket.messages';

import { BadgingService } from '../../services/badging/badging.service';
import { BrowserService } from '../../services/browser.service';
import { ChatService } from '../../services/chat.service';
import { DebuggerService } from '../../services/debugger.service';
import { EmoteService } from '../../services/emote/emote.service';
import { RtcStatsService } from '../../services/rtc-stats.service';
import { SceneService } from '../../services/scene.service';
import { TeleportService } from '../../services/teleport.service';
import { TimerService } from '../../services/timer.service';
import { WebsocketService } from '../../services/websocket.service';
import { Player } from '../../social-pixi-canvas/player';
import { ToastBannerMessageComponent } from '../../toast-banner-message/toast-banner-message.component';
import { RoomSizeType } from '../../types/room';
import { DEFAULT_TOASTER, SUCCESS_TOASTER, WARNING_TOASTER, toastTypes } from '../../types/toast-banners';
import { Renderer } from 'pixi.js';
import { actions, socialAppSelectors } from '../../state/app';

enum TestUserCommand {
  GROUP_SHARE = 'group-share',
  END_SHARE = 'end-share',
}

@Component({
  selector: 'test-utilities',
  templateUrl: './test-utilities.component.html',
  styleUrls: ['./test-utilities.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestUtilitiesComponent extends Loggable implements OnDestroy, OnInit {
  private websocketService = inject(WebsocketService);
  private sceneService = inject(SceneService);
  private rtcStatsService = inject(RtcStatsService);
  private chatService = inject(ChatService);
  private browserService = inject(BrowserService);
  private timerService = inject(TimerService);
  private debuggerService = inject(DebuggerService);
  private store = inject<Store<AppState>>(Store);
  private overlay = inject(Overlay);
  private teleportService = inject(TeleportService);
  private badgingService = inject(BadgingService);
  private emoteService = inject(EmoteService);

  private destroyed$ = new ReplaySubject<boolean>(1);
  private fakeUserId = 0;

  public readonly selectedTestUserCommand = signal(TestUserCommand.GROUP_SHARE);
  public readonly teleportX = signal('0');
  public readonly teleportY = signal('0');
  public TestUserCommand = TestUserCommand;
  public readonly testUserCommandData = signal('');

  public readonly bannerType = signal('');
  public readonly bannerMessage = signal('');
  public readonly bannerIcon = signal('');
  public readonly bannerSVGIcon = signal('');
  private overlayRef?: OverlayRef;
  private portalRef?: ComponentPortal<ToastBannerMessageComponent>;
  private ref?: ComponentRef<ToastBannerMessageComponent>;

  public readonly player = signal<Player | undefined>(undefined);
  public readonly isMovingTimeoutDuration = signal<number | undefined>(undefined);

  public RoomSizeType = RoomSizeType;
  public readonly triggerOnboardingExperience = signal(false);

  public readonly disableCaptureMedia = signal(false);

  public constructor() {
    super();
    this.setDisplayName('TestUtilitiesComponent');
  }

  public ngOnInit(): void {
    this.player.set(this.sceneService.getPlayer());
    this.isMovingTimeoutDuration.set(this.player()?.isMovingTimeoutDuration);
    this.debuggerService.disableCaptureMedia.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (result: boolean) => {
        this.disableCaptureMedia.set(result);
      },
    });
    this.store
      .select(socialAppSelectors.showOnboardingExperience)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((triggerOnboardingExperience) => this.triggerOnboardingExperience.set(triggerOnboardingExperience));
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public isMovingTimeoutDurationUpdate(): void {
    if (this.player() && this.isMovingTimeoutDuration()) {
      this.player()!.isMovingTimeoutDuration = this.isMovingTimeoutDuration() as number;
      this.websocketService.sendMessage({
        t: OutgoingMessageType.SET_MOVEMENT_END_DURATION,
        duration: this.isMovingTimeoutDuration() as number,
      });
    }
  }

  public onMuteTestUsers(muted: boolean): void {
    const msg: SetTestUserAdioMessage = {
      t: OutgoingMessageType.SET_TU_AUDIO,
      muted,
    };
    this.websocketService.sendMessage(msg);
  }

  public onTestUserCommandChange(change: MatSelectChange): void {
    this.selectedTestUserCommand.set(change.value);
  }

  public onSendTestUserCommand(): void {
    try {
      const msg: SendTestUserCommandMessage = {
        t: OutgoingMessageType.SEND_TU_CMD,
        command: this.selectedTestUserCommand(),
        data: this.testUserCommandData() === '' ? '' : JSON.stringify(JSON.parse(this.testUserCommandData())),
      };
      this.websocketService.sendMessage(msg);
    } catch (e) {
      this.error('Error sending test user command', e);
    }
  }

  public onResizeRoom(size: RoomSizeType): void {
    const msg: ResizeMessage = {
      t: IncomingMessageType.RESIZE,
      width: size,
      height: size,
    };
    this.websocketService.simulateMessage(msg);
  }

  public onCreateUsers(count: number): void {
    if (!this.sceneService.sceneManager) {
      return;
    }
    for (let i = 0; i < count; i++) {
      this.fakeUserId++;
      const player = new Player({
        store: this.store,
        renderer: this.sceneService.sceneManager.app!.renderer as unknown as Renderer,
        badgingService: this.badgingService,
        user: { userId: this.fakeUserId.toString(), name: `Test User ${this.fakeUserId}` },
        isPrimary: false,
        browserService: this.browserService,
        chatService: this.chatService,
        rtcStatsService: this.rtcStatsService,
        videoElement: undefined,
        x: (Math.random() - 0.5) * 4000,
        y: (Math.random() - 0.5) * 4000,
        bounds: this.sceneService.sceneManager.bounds,
        emoteService: this.emoteService,
        sprites: this.sceneService.sceneManager.sprites,
      });
      this.sceneService.sceneManager.addParticipant(player);
    }
  }

  public onRemoveFakeUser(removeAll = false) {
    if (removeAll) {
      const participants = this.sceneService.sceneManager?.participantsList;
      participants?.forEach((participant) => {
        if (!isNaN(Number(participant.userId))) {
          this.sceneService.sceneManager?.removeParticipant(participant.userId);
        }
      });
      this.fakeUserId = 0;
      return;
    }
    this.sceneService.sceneManager?.removeParticipant(this.fakeUserId.toString());
    this.fakeUserId--;
  }

  public onSetShowTimer(show: boolean): void {
    if (show) {
      this.timerService.showTimer();
    } else {
      this.timerService.hideTimer();
    }
  }

  public async onDumpState(): Promise<void> {
    await this.debuggerService.onDumpState();
  }

  public async onCaptureMedia(): Promise<void> {
    await this.debuggerService.captureMediaRecording();
  }

  public onTeleport(): void {
    this.teleportService.onTeleport(this.teleportX(), this.teleportY());
  }

  public showNewBanner(): void {
    this.overlayRef = this.overlay.create({ width: '100%' });
    this.portalRef = new ComponentPortal(ToastBannerMessageComponent);
    this.ref = this.overlayRef.attach(this.portalRef);
    this.ref.instance.message.set(this.bannerMessage());
    this.ref.instance.icon.set(this.bannerIcon());
    this.ref.instance.svgIcon.set(this.bannerSVGIcon());

    switch (this.bannerType()) {
      case toastTypes.SUCCESS:
        this.ref.instance.messageType = toastTypes.SUCCESS;
        this.ref.instance.toasterConfig = SUCCESS_TOASTER;
        break;
      case toastTypes.WARNING:
        this.ref.instance.messageType = toastTypes.WARNING;
        this.ref.instance.toasterConfig = WARNING_TOASTER;
        break;
      default:
        this.ref.instance.messageType = toastTypes.DEFAULT;
        this.ref.instance.toasterConfig = DEFAULT_TOASTER;
    }
  }

  public removeBanner(): void {
    this.overlayRef?.detach();
    this.portalRef = undefined;
    this.overlayRef = undefined;
    this.ref = undefined;
  }

  public triggerOnboardingExperienceChange(change: MatSelectChange): void {
    this.triggerOnboardingExperience.set(change.value);
    this.store.dispatch(actions.SHOW_ONBOARDING_EXPERIENCE({ showOnboardingExperience: change.value }));
  }
}
